import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Building2, FileText, Download, Eye, ExternalLink, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyOrganizationArea = () => {
  const { token } = useAuth();
  const [orgInfo, setOrgInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      
      // Fetch org info
      const orgRes = await axios.get(`${API_URL}/api/my-organization`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (orgRes.data.has_organization) {
        setOrgInfo(orgRes.data);
        
        // Fetch documents
        const docsRes = await axios.get(`${API_URL}/api/my-org-documents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDocuments(docsRes.data);
      }
    } catch (error) {
      console.error('Error fetching org data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!orgInfo?.has_organization) {
    return null; // Non mostrare nulla se l'utente non appartiene a un'organizzazione
  }

  return (
    <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/30 rounded-2xl p-4 mb-6">
      {/* Header con logo organizzazione */}
      <div className="flex items-center gap-4 mb-4">
        {orgInfo.organization_logo ? (
          <img 
            src={orgInfo.organization_logo} 
            alt={orgInfo.organization_name}
            className="w-12 h-12 rounded-xl object-contain bg-white p-1"
          />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
        )}
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            Area {orgInfo.organization_name}
            <Sparkles className="w-4 h-4 text-amber-400" />
          </h3>
          <p className="text-cyan-400 text-sm">Contenuti esclusivi per te</p>
        </div>
      </div>

      {/* Documenti */}
      {documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => (
            <a
              key={doc.id}
              href={doc.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 bg-slate-900/50 hover:bg-slate-800/50 rounded-xl transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-white font-medium group-hover:text-cyan-400 transition-colors">
                    {doc.title}
                  </p>
                  {doc.description && (
                    <p className="text-xs text-slate-500">{doc.description}</p>
                  )}
                  <p className="text-xs text-slate-600">
                    {doc.document_type} • {new Date(doc.created_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
              <ExternalLink className="w-5 h-5 text-slate-500 group-hover:text-cyan-400 transition-colors" />
            </a>
          ))}
        </div>
      ) : (
        <div className="text-center py-6">
          <FileText className="w-10 h-10 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">
            Nessun documento disponibile al momento
          </p>
        </div>
      )}
    </div>
  );
};

export default MyOrganizationArea;
